import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import { getIngredients } from "../../insumos/api/ingredientsApi";
import {
  createPreparedProductWithRecipe,
  getPreparedProducts,
} from "../api/preparedProductsApi";
import {
  MAX_DECIMAL_VALUE,
  formatDecimalInput,
  normalizeDecimalInput,
  parseDecimalInput,
} from "../utils/preparedProductForm.utils";
import {
  parsePreparedProductRecipeNotes,
  serializePreparedProductRecipeNotes,
} from "../utils/preparedProductRecipeNotes";
import {
  normalizePreparedProductSectors,
  PREPARED_PRODUCT_SECTORS,
} from "../utils/preparedProductSector.utils";

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;
const MAX_COMPONENTS_PER_TYPE = 200;
const UNIT_LABELS = { g: "g", ml: "ml", unit: "unit", package: "paq" };
const RESULT_UNITS = [
  { value: "g", label: "Gramos (g)" },
  { value: "ml", label: "Mililitros (ml)" },
  { value: "unit", label: "Unidad" },
  { value: "package", label: "Paquete" },
];

function unwrapHttp(response) {
  if (response && typeof response === "object" && "data" in response && !("meta" in response)) {
    return response.data;
  }
  return response;
}

function responseRows(response) {
  const data = unwrapHttp(response);
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  return Array.isArray(data) ? data : [];
}

function errorToMessage(error) {
  const message =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message;
  return Array.isArray(message) ? message.join(". ") : message || "No se pudo crear el elaborado.";
}

function UnitSelector({ value, onChange, disabled }) {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      disabled={disabled}
      onChange={(_, nextValue) => nextValue && onChange(nextValue)}
      aria-label="Unidad de medida"
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 0.8,
        "& .MuiToggleButtonGroup-grouped": {
          minWidth: 0,
          height: 32,
          border: "1px solid rgba(64,64,94,.18) !important",
          borderRadius: "10px !important",
          color: "primary.900",
          fontSize: 11,
          fontWeight: 800,
          textTransform: "none",
          "&.Mui-selected": {
            bgcolor: "primary.500",
            color: "logoblanco",
            "&:hover": { bgcolor: "primary.600" },
          },
        },
      }}
    >
      {RESULT_UNITS.map((unit) => (
        <ToggleButton
          key={unit.value}
          value={unit.value}
        >
          {unit.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

function SectorSelector({ value, onChange, disabled, error }) {
  return (
    <Box>
      <Typography sx={{ mb: 0.8, fontSize: 12, fontWeight: 800, color: "neutral.700" }}>
        Sector
      </Typography>
      <ToggleButtonGroup
        value={value}
        disabled={disabled}
        onChange={(_, nextValue) => onChange(normalizePreparedProductSectors(nextValue))}
        aria-label="Sectores del elaborado"
        sx={{
          gap: 1,
          "& .MuiToggleButtonGroup-grouped": {
            minWidth: 108,
            border: "1px solid rgba(64,64,94,.18) !important",
            borderRadius: "10px !important",
            py: 0.8,
            color: "primary.900",
            fontSize: 12,
            fontWeight: 800,
            textTransform: "none",
            "&.Mui-selected": {
              bgcolor: "primary.500",
              color: "logoblanco",
              "&:hover": { bgcolor: "primary.600" },
            },
          },
        }}
      >
        {PREPARED_PRODUCT_SECTORS.map((sector) => (
          <ToggleButton key={sector} value={sector}>
            {sector}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Typography
        sx={{
          minHeight: 17,
          mt: 0.35,
          fontSize: 11,
          color: error ? "error.main" : "neutral.600",
        }}
      >
        {error || "Podés seleccionar uno o ambos sectores"}
      </Typography>
    </Box>
  );
}

function ComponentRow({ item, error, disabled, onChange, onRemove }) {
  const [notesOpen, setNotesOpen] = useState(Boolean(item.notes));

  return (
    <Box sx={{ py: 0.7, borderBottom: "1px solid rgba(64,64,94,.12)" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(120px, 1fr) 135px 38px 34px",
          gap: 0.7,
          alignItems: "center",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap title={item.name} sx={{ fontSize: 12.5, fontWeight: 800 }}>
            {item.name}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: "neutral.600" }}>
            {item.kind === "ingredient" ? "Insumo" : "Elaborado"}
          </Typography>
        </Box>
        <TextField
          value={item.quantity}
          onChange={(event) => onChange({ quantity: normalizeDecimalInput(event.target.value) })}
          onBlur={() => {
            const value = parseDecimalInput(item.quantity);
            if (value !== null) onChange({ quantity: formatDecimalInput(value) });
          }}
          error={Boolean(error)}
          disabled={disabled}
          placeholder="Cantidad"
          size="small"
          inputProps={{ inputMode: "decimal", "aria-label": `Cantidad de ${item.name}` }}
          InputProps={{
            endAdornment: <InputAdornment position="end">{UNIT_LABELS[item.unit] || item.unit}</InputAdornment>,
          }}
          sx={{
            "& .MuiOutlinedInput-root": { height: 34, borderRadius: "10px", fontSize: 12 },
            "& .MuiInputAdornment-root": { fontSize: 11, fontWeight: 700 },
          }}
        />
        <IconButton
          size="small"
          aria-label={`Mostrar notas de ${item.name}`}
          onClick={() => setNotesOpen((current) => !current)}
          disabled={disabled}
          sx={{ transform: notesOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={`Quitar ${item.name}`}
          onClick={onRemove}
          disabled={disabled}
          sx={{ color: "error.main" }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
      {error && <Typography sx={{ mt: 0.3, fontSize: 10.5, color: "error.main" }}>{error}</Typography>}
      <Collapse in={notesOpen}>
        <TextField
          value={item.notes}
          onChange={(event) => onChange({ notes: event.target.value })}
          placeholder="Nota opcional para este componente"
          size="small"
          fullWidth
          disabled={disabled}
          inputProps={{ maxLength: 500 }}
          sx={{ mt: 0.65, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 12 } }}
        />
      </Collapse>
    </Box>
  );
}

function formSignature({
  name,
  sector,
  yieldPercent,
  resultQuantity,
  resultUnit,
  isActive,
  recipeDescription,
  recipeSteps,
  components,
}) {
  return JSON.stringify({
    name,
    sector: normalizePreparedProductSectors(sector),
    yieldPercent: formatDecimalInput(parseDecimalInput(yieldPercent), 2),
    resultQuantity: formatDecimalInput(parseDecimalInput(resultQuantity)),
    resultUnit,
    isActive,
    recipeDescription,
    recipeSteps: recipeSteps.map((step) => step.text),
    components: components.map((component) => ({
      kind: component.kind,
      id: component.id,
      quantity: formatDecimalInput(parseDecimalInput(component.quantity)),
      notes: component.notes || "",
    })),
  });
}

function componentErrorKey(component) {
  return `component-${component.kind}-${component.id}`;
}

export default function CreatePreparedProductPage({
  initialValues,
  pageTitle = "Crear Elaborados",
  showStatus = false,
  successMessage = "El elaborado se creó correctamente.",
  onSave,
  excludedPreparedProductId,
  allowInactiveWithoutRecipe = false,
}) {
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const recipeStepKeyRef = useRef(1);
  const initialRecipeNotes = useMemo(
    () => parsePreparedProductRecipeNotes(initialValues?.notes),
    [initialValues?.notes],
  );
  const [name, setName] = useState(initialValues?.name || "");
  const [sector, setSector] = useState(() =>
    normalizePreparedProductSectors(initialValues?.sector || ["Cocina"]),
  );
  const [yieldPercent, setYieldPercent] = useState(
    formatDecimalInput(initialValues?.yieldPercent ?? "", 2),
  );
  const [resultQuantity, setResultQuantity] = useState(
    formatDecimalInput(initialValues?.resultQuantity ?? ""),
  );
  const [resultUnit, setResultUnit] = useState(initialValues?.resultUnit || "g");
  const [isActive, setIsActive] = useState(initialValues?.isActive ?? true);
  const [recipeDescription, setRecipeDescription] = useState(initialRecipeNotes.description);
  const [recipeSteps, setRecipeSteps] = useState(() => initialRecipeNotes.steps.map((text, index) => ({
    key: `existing-step-${index}`,
    text,
  })));
  const [components, setComponents] = useState(() =>
    (initialValues?.components || []).map((component) => ({
      ...component,
      quantity: formatDecimalInput(component.quantity),
      notes: component.notes || "",
    })),
  );
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const currentSignature = useMemo(
    () => formSignature({
      name,
      sector,
      yieldPercent,
      resultQuantity,
      resultUnit,
      isActive,
      recipeDescription,
      recipeSteps,
      components,
    }),
    [components, isActive, name, recipeDescription, recipeSteps, resultQuantity, resultUnit, sector, yieldPercent],
  );
  const initialSignatureRef = useRef(currentSignature);
  const dirty = currentSignature !== initialSignatureRef.current;

  useEffect(() => {
    if (!dirty) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    let restoringHistory = false;
    const handlePopState = () => {
      if (restoringHistory) {
        restoringHistory = false;
        return;
      }
      if (!window.confirm("¿Seguro que querés salir? Los cambios realizados se perderán.")) {
        restoringHistory = true;
        window.history.forward();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [dirty]);

  const selectedKeys = useMemo(
    () => new Set(components.map((item) => `${item.kind}:${item.id}`)),
    [components],
  );

  useEffect(() => {
    const term = search.trim();
    if (!term) {
      requestIdRef.current += 1;
      setOptions([]);
      setSearching(false);
      setSearchError("");
      return undefined;
    }

    const controller = new AbortController();
    const requestId = ++requestIdRef.current;
    const timeout = setTimeout(async () => {
      setSearching(true);
      setSearchError("");
      const results = await Promise.allSettled([
        getIngredients({ page: 1, limit: SEARCH_LIMIT, name: term, isActive: true, signal: controller.signal }),
        getPreparedProducts({ page: 1, limit: SEARCH_LIMIT, name: term, isActive: true, signal: controller.signal }),
      ]);

      if (controller.signal.aborted || requestId !== requestIdRef.current) return;

      const nextOptions = [];
      if (results[0].status === "fulfilled") {
        responseRows(results[0].value).forEach((ingredient) => {
          nextOptions.push({
            kind: "ingredient",
            group: "Insumos",
            id: ingredient.id,
            name: ingredient.name,
            unit: ingredient.baseUnit,
          });
        });
      }
      if (results[1].status === "fulfilled") {
        responseRows(results[1].value).forEach((preparedProduct) => {
          if (Number(preparedProduct.id) === Number(excludedPreparedProductId)) return;
          nextOptions.push({
            kind: "preparedProduct",
            group: "Elaborados",
            id: preparedProduct.id,
            name: preparedProduct.name,
            unit: preparedProduct.resultUnit,
          });
        });
      }

      setOptions(nextOptions);
      if (results.every((result) => result.status === "rejected")) {
        setSearchError("No se pudo consultar el catálogo.");
      } else if (results.some((result) => result.status === "rejected")) {
        setSearchError("Una parte del catálogo no pudo cargarse.");
      }
      setSearching(false);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [excludedPreparedProductId, search]);

  function addComponent(option) {
    if (!option) return;
    const key = `${option.kind}:${option.id}`;
    if (selectedKeys.has(key)) return;
    const sameTypeCount = components.filter((component) => component.kind === option.kind).length;
    if (sameTypeCount >= MAX_COMPONENTS_PER_TYPE) {
      setErrors((current) => ({
        ...current,
        components: `La receta admite hasta ${MAX_COMPONENTS_PER_TYPE} ${option.kind === "ingredient" ? "insumos" : "elaborados"}.`,
      }));
      return;
    }
    setComponents((current) => [...current, { ...option, quantity: "", notes: "" }]);
    setSearch("");
    setErrors((current) => ({ ...current, components: "" }));
  }

  function updateComponent(index, changes) {
    const component = components[index];
    setComponents((current) =>
      current.map((component, componentIndex) =>
        componentIndex === index ? { ...component, ...changes } : component,
      ),
    );
    if (component) {
      setErrors((current) => ({ ...current, [componentErrorKey(component)]: "" }));
    }
  }

  function addRecipeStep() {
    setRecipeSteps((current) => [...current, {
      key: `new-step-${recipeStepKeyRef.current++}`,
      text: "",
    }]);
  }

  function updateRecipeStep(index, text) {
    setRecipeSteps((current) => current.map((step, stepIndex) =>
      stepIndex === index ? { ...step, text } : step));
    setErrors((current) => ({ ...current, notes: "" }));
  }

  function moveRecipeStep(index, direction) {
    setRecipeSteps((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function recipeNotesForPayload() {
    const descriptionUnchanged = recipeDescription === initialRecipeNotes.description;
    const currentSteps = recipeSteps.map((step) => step.text);
    const stepsUnchanged = currentSteps.length === initialRecipeNotes.steps.length &&
      currentSteps.every((step, index) => step === initialRecipeNotes.steps[index]);
    if (initialValues?.notes && descriptionUnchanged && stepsUnchanged) {
      return initialValues.notes;
    }
    return serializePreparedProductRecipeNotes(recipeDescription, recipeSteps);
  }

  function validate() {
    const nextErrors = {};
    const normalizedName = name.trim();
    const yieldValue = parseDecimalInput(yieldPercent);
    const resultValue = parseDecimalInput(resultQuantity);
    const serializedRecipeNotes = recipeNotesForPayload();
    const hasRecipeDraft = Boolean(
      String(resultQuantity).trim() || components.length || serializedRecipeNotes,
    );
    const recipeRequired = Boolean(
      !allowInactiveWithoutRecipe || initialValues?.hasRecipe || isActive || hasRecipeDraft,
    );

    if (!normalizedName) nextErrors.name = "Ingresá un nombre.";
    else if (normalizedName.length > 200) nextErrors.name = "El nombre no puede superar 200 caracteres.";
    if (!sector.length) nextErrors.sector = "Seleccioná Cocina, Barra o ambos.";
    if (recipeRequired && (yieldValue === null || yieldValue <= 0)) {
      nextErrors.yieldPercent = "Ingresá un rendimiento mayor a cero.";
    }
    if (recipeRequired && (resultValue === null || resultValue <= 0)) {
      nextErrors.resultQuantity = "Ingresá una cantidad mayor a cero.";
    } else if (resultValue !== null && resultValue > MAX_DECIMAL_VALUE) {
      nextErrors.resultQuantity = "La cantidad es demasiado grande.";
    }
    if (recipeRequired && !components.length) {
      nextErrors.components = "Agregá al menos un insumo o elaborado.";
    }
    const ingredientCount = components.filter((component) => component.kind === "ingredient").length;
    const preparedProductCount = components.length - ingredientCount;
    if (ingredientCount > MAX_COMPONENTS_PER_TYPE || preparedProductCount > MAX_COMPONENTS_PER_TYPE) {
      nextErrors.components = `La receta admite hasta ${MAX_COMPONENTS_PER_TYPE} componentes de cada tipo.`;
    }
    if ((serializedRecipeNotes?.length || 0) > 16000) {
      nextErrors.notes = "La descripción y los pasos no pueden superar 16000 caracteres.";
    }

    components.forEach((component, index) => {
      const quantity = parseDecimalInput(component.quantity);
      if (component.isAvailable === false) {
        nextErrors[componentErrorKey(component)] = "Este componente está inactivo. Reemplazalo o quitálo.";
      } else if (quantity === null || quantity <= 0) {
        nextErrors[componentErrorKey(component)] = "La cantidad debe ser mayor a cero.";
      } else if (quantity > MAX_DECIMAL_VALUE) {
        nextErrors[componentErrorKey(component)] = "La cantidad es demasiado grande.";
      }
    });

    setErrors(nextErrors);
    return {
      valid: Object.keys(nextErrors).length === 0,
      normalizedName,
      yieldValue,
      resultValue,
      recipeRequired,
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (saving) return;
    const validation = validate();
    if (!validation.valid) return;

    const yieldFactor = validation.yieldValue === null
      ? null
      : Number((validation.yieldValue / 100).toFixed(4));
    const recipeInputsUnchanged = validation.recipeRequired && initialValues &&
      formatDecimalInput(validation.yieldValue, 2) ===
        formatDecimalInput(initialValues.yieldPercent, 2) &&
      formatDecimalInput(validation.resultValue) ===
        formatDecimalInput(initialValues.resultQuantity);
    const recipeQuantity = validation.recipeRequired
      ? recipeInputsUnchanged && initialValues.recipeQuantity
        ? Number(initialValues.recipeQuantity)
        : Number((validation.resultValue / yieldFactor).toFixed(4))
      : null;
    if (validation.recipeRequired && (!Number.isFinite(recipeQuantity) || recipeQuantity <= 0 || recipeQuantity > MAX_DECIMAL_VALUE)) {
      setErrors((current) => ({ ...current, yieldPercent: "La combinación de rendimiento y cantidad excede el límite permitido." }));
      return;
    }

    const ingredients = components
      .filter((component) => component.kind === "ingredient")
      .map((component) => ({
        ingredientId: Number(component.id),
        quantity: parseDecimalInput(component.quantity),
        notes: component.notes.trim() || null,
      }));
    const preparedProducts = components
      .filter((component) => component.kind === "preparedProduct")
      .map((component) => ({
        componentPreparedProductId: Number(component.id),
        quantity: parseDecimalInput(component.quantity),
        notes: component.notes.trim() || null,
      }));

    setSaving(true);
    setSubmitError("");
    try {
      const payload = {
        name: validation.normalizedName,
        sector,
        resultUnit,
        yieldFactor,
        isActive,
        recipe: validation.recipeRequired ? {
          recipeQuantity,
          resultQuantity: validation.resultValue,
          notes: recipeNotesForPayload(),
          isActive: isActive || initialValues?.recipeIsActive !== false,
          ingredients,
          preparedProducts,
        } : null,
      };
      if (onSave) await onSave(payload);
      else await createPreparedProductWithRecipe(payload);
      navigate("/stock/base-de-datos/elaborados", {
        replace: true,
        state: { successMessage },
      });
    } catch (error) {
      setSubmitError(errorToMessage(error));
    } finally {
      setSaving(false);
    }
  }

  const componentCounts = components.reduce(
    (counts, component) => ({ ...counts, [component.kind]: (counts[component.kind] || 0) + 1 }),
    {},
  );
  const visibleOptions = options.filter((option) =>
    !selectedKeys.has(`${option.kind}:${option.id}`) &&
    (componentCounts[option.kind] || 0) < MAX_COMPONENTS_PER_TYPE,
  );

  function requestCancel() {
    if (dirty) {
      setCancelDialogOpen(true);
      return;
    }
    navigate("/stock/base-de-datos/elaborados");
  }

  return (
    <Stack
      noValidate
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        "@media (max-height: 700px)": {
          p: 1.25,
          gap: 1,
        },
      }}
      component="form"
      onSubmit={handleSubmit}
    >
      <StockPageHeader title={pageTitle} onBack={requestCancel} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          bgcolor: "transparent",
          borderRadius: 0,
          p: 0,
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
            bgcolor: "neutral.50",
            borderRadius: "22px",
            p: { xs: 2, md: 3 },
            "@media (max-height: 700px)": {
              p: 1.5,
            },
            "& .MuiOutlinedInput-root": {
              bgcolor: "common.white",
            },
          }}
        >
          <Stack
            spacing={2.5}
            sx={{
              height: "100%",
              minHeight: 0,
              "@media (max-height: 700px)": {
                gap: 1.25,
              },
            }}
          >
          {submitError ? (
            <Alert severity="error" onClose={() => setSubmitError("")}>{submitError}</Alert>
          ) : null}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.72fr 1.28fr" },
              gap: { xs: 3, md: 4 },
              alignItems: "stretch",
              overflowY: "auto",
              overflowX: "hidden",
              pr: { md: 0.75 },
              scrollbarGutter: "stable",
              "@media (max-height: 700px)": {
                gap: 2,
              },
            }}
          >
            <Stack
              spacing={2.1}
              sx={{
                minWidth: 0,
                "@media (max-height: 700px)": {
                  gap: 1.15,
                },
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 900 }}>Información General</Typography>
              <TextField
                label="Nombre"
                value={name}
                onChange={(event) => {
                  setName(event.target.value.slice(0, 200));
                  setErrors((current) => ({ ...current, name: "" }));
                }}
                disabled={saving}
                error={Boolean(errors.name)}
                helperText={errors.name || `${name.length}/200`}
                size="small"
                fullWidth
                inputProps={{ maxLength: 200 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
              <SectorSelector
                value={sector}
                onChange={(value) => {
                  setSector(value);
                  setErrors((current) => ({ ...current, sector: "" }));
                }}
                disabled={saving}
                error={errors.sector}
              />
              <TextField
                label="Rendimiento"
                value={yieldPercent}
                onChange={(event) => {
                  setYieldPercent(normalizeDecimalInput(event.target.value, 2));
                  setErrors((current) => ({ ...current, yieldPercent: "" }));
                }}
                onBlur={() => {
                  const value = parseDecimalInput(yieldPercent);
                  if (value !== null) setYieldPercent(formatDecimalInput(value, 2));
                }}
                disabled={saving}
                error={Boolean(errors.yieldPercent)}
                helperText={errors.yieldPercent || "Porcentaje aprovechable de la receta"}
                size="small"
                fullWidth
                inputProps={{ inputMode: "decimal", maxLength: 11 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
              <Stack spacing={0.8}>
                <TextField
                  label="Cantidad generada"
                  value={resultQuantity}
                  onChange={(event) => {
                    setResultQuantity(normalizeDecimalInput(event.target.value));
                    setErrors((current) => ({ ...current, resultQuantity: "" }));
                  }}
                  onBlur={() => {
                    const value = parseDecimalInput(resultQuantity);
                    if (value !== null) setResultQuantity(formatDecimalInput(value));
                  }}
                  disabled={saving}
                  error={Boolean(errors.resultQuantity)}
                  helperText={errors.resultQuantity || "Cantidad que produce esta receta"}
                  size="small"
                  fullWidth
                  inputProps={{ inputMode: "decimal", maxLength: 14 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">{UNIT_LABELS[resultUnit]}</InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
                <UnitSelector value={resultUnit} onChange={setResultUnit} disabled={saving} />
              </Stack>
              {showStatus ? (
                <Box>
                  <Typography sx={{ mb: 0.8, fontSize: 12, fontWeight: 800, color: "neutral.700" }}>
                    Estado
                  </Typography>
                  <ToggleButtonGroup
                    value={isActive ? "active" : "inactive"}
                    exclusive
                    disabled={saving}
                    onChange={(_, value) => value && setIsActive(value === "active")}
                    sx={{
                      gap: 1,
                      "& .MuiToggleButtonGroup-grouped": {
                        minWidth: 108,
                        border: "1px solid rgba(64,64,94,.18) !important",
                        borderRadius: "10px !important",
                        py: 0.8,
                        color: "primary.900",
                        fontSize: 12,
                        fontWeight: 800,
                        textTransform: "none",
                        "&.Mui-selected": {
                          bgcolor: "primary.500",
                          color: "logoblanco",
                          "&:hover": { bgcolor: "primary.600" },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="active">Activo</ToggleButton>
                    <ToggleButton value="inactive">Inactivo</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              ) : null}
              {allowInactiveWithoutRecipe && !initialValues?.hasRecipe ? (
                <Alert severity="info" sx={{ borderRadius: "12px", fontSize: 12 }}>
                  Este elaborado todavía no tiene receta. Completala para mantenerlo activo, o
                  desactivalo para guardar solamente la información general.
                </Alert>
              ) : null}
            </Stack>

            <Stack
              spacing={1.2}
              sx={{
                minWidth: 0,
                pl: { md: 4 },
                borderLeft: { xs: "none", md: "2px solid rgba(2,8,23,.35)" },
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 900 }}>Receta Elaborado</Typography>
                <Typography sx={{ fontSize: 11.5, color: "neutral.700" }}>Agregá un insumo o elaborado</Typography>
              </Box>
              <Autocomplete
                options={visibleOptions}
                value={null}
                inputValue={search}
                onInputChange={(_, value, reason) => {
                  if (reason !== "reset") setSearch(value);
                }}
                onChange={(_, option) => addComponent(option)}
                loading={searching}
                disabled={saving}
                filterOptions={(items) => items}
                groupBy={(option) => option.group}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => option.kind === value.kind && option.id === value.id}
                noOptionsText={search.trim() ? "Sin resultados" : "Escribí para buscar"}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <Box
                      sx={{
                        position: "sticky",
                        top: -8,
                        zIndex: 1,
                        px: 1.5,
                        py: 0.7,
                        bgcolor: "secondary.100",
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 900, color: "primary.900" }}>
                        {params.group}
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ p: 0, m: 0 }}>
                      {params.children}
                    </Box>
                  </li>
                )}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box
                      component="li"
                      key={`${option.kind}:${option.id}`}
                      {...optionProps}
                      sx={{ display: "flex", gap: 2 }}
                    >
                      <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700 }}>
                        {option.name}
                      </Typography>
                      <Typography
                        sx={{
                          flexShrink: 0,
                          alignSelf: "center",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "neutral.600",
                        }}
                      >
                        {UNIT_LABELS[option.unit] || option.unit}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Buscá un insumo o elaborado"
                    size="small"
                    error={Boolean(errors.components)}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {searching ? <CircularProgress size={17} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {(errors.components || searchError) && (
                <Typography sx={{ fontSize: 11, color: errors.components ? "error.main" : "warning.main" }}>
                  {errors.components || searchError}
                </Typography>
              )}

              <Box
                sx={{
                  maxHeight: { xs: 300, md: "clamp(110px, 19vh, 190px)" },
                  minHeight: 0,
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography sx={{ mb: 0.3, fontSize: 12, fontWeight: 800 }}>Lista de ingredientes</Typography>
                <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", pr: 0.7 }}>
                  {components.length ? (
                    components.map((component, index) => (
                      <ComponentRow
                        key={`${component.kind}:${component.id}`}
                        item={component}
                        error={errors[componentErrorKey(component)]}
                        disabled={saving}
                        onChange={(changes) => updateComponent(index, changes)}
                        onRemove={() => {
                          setComponents((current) => current.filter((_, itemIndex) => itemIndex !== index));
                          setErrors((current) => ({
                            ...current,
                            [componentErrorKey(component)]: "",
                          }));
                        }}
                      />
                    ))
                  ) : (
                    <Box sx={{ px: 2, py: 2.2, borderRadius: "14px", bgcolor: "secondary.50", border: "1px dashed rgba(64,64,94,.2)" }}>
                      <Typography sx={{ fontSize: 12, color: "neutral.600" }}>
                        Buscá y seleccioná los componentes de la receta.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Divider />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.6 }}>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 800 }}>Preparación</Typography>
                    <Typography sx={{ fontSize: 10.5, color: "neutral.600" }}>
                      La descripción y los pasos son opcionales.
                    </Typography>
                  </Box>
                  <Button
                    type="button"
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={saving}
                    onClick={addRecipeStep}
                    sx={{ textTransform: "none", fontWeight: 800 }}
                  >
                    Agregar paso
                  </Button>
                </Stack>
                <TextField
                  label="Descripción (opcional)"
                  value={recipeDescription}
                  onChange={(event) => {
                    setRecipeDescription(event.target.value);
                    setErrors((current) => ({ ...current, notes: "" }));
                  }}
                  disabled={saving}
                  multiline
                  minRows={2}
                  maxRows={4}
                  size="small"
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
                {recipeSteps.length ? (
                  <Stack spacing={0.8} sx={{ mt: 1 }}>
                    {recipeSteps.map((step, index) => (
                      <Stack key={step.key} direction="row" spacing={0.5} alignItems="center">
                        <TextField
                          label={`Paso ${index + 1}`}
                          value={step.text}
                          onChange={(event) => updateRecipeStep(index, event.target.value)}
                          disabled={saving}
                          size="small"
                          fullWidth
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        />
                        <IconButton type="button" size="small" disabled={saving || index === 0} aria-label={`Subir paso ${index + 1}`} onClick={() => moveRecipeStep(index, -1)}>
                          <KeyboardArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton type="button" size="small" disabled={saving || index === recipeSteps.length - 1} aria-label={`Bajar paso ${index + 1}`} onClick={() => moveRecipeStep(index, 1)}>
                          <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                        <IconButton type="button" size="small" color="error" disabled={saving} aria-label={`Quitar paso ${index + 1}`} onClick={() => setRecipeSteps((current) => current.filter((_, stepIndex) => stepIndex !== index))}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                ) : null}
                {errors.notes ? (
                  <Typography sx={{ mt: 0.5, fontSize: 11, color: "error.main" }}>
                    {errors.notes}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          </Box>

          <Divider sx={{ flexShrink: 0 }} />
          <Stack
            direction="row"
            spacing={1.2}
            sx={{ flexShrink: 0, justifyContent: "flex-end", bgcolor: "neutral.50" }}
          >
            <Button
              type="button"
              variant="outlined"
              disabled={saving}
              onClick={requestCancel}
              sx={{ minWidth: 110, borderRadius: "22px", textTransform: "none", fontWeight: 800 }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{ minWidth: 124, borderRadius: "22px", textTransform: "none", fontWeight: 800 }}
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </Stack>
          </Stack>
        </Box>
      </Box>

      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: "18px", maxWidth: 430 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: "primary.900" }}>
          ¿Seguro que querés cancelar?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "neutral.700" }}>
            Los cambios realizados se perderán y no podrán recuperarse.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            type="button"
            onClick={() => setCancelDialogOpen(false)}
            sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 800 }}
          >
            Seguir editando
          </Button>
          <Button
            type="button"
            variant="contained"
            color="error"
            onClick={() => navigate("/stock/base-de-datos/elaborados")}
            sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 800 }}
          >
            Descartar cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
